import { v2InvestorExportMcpData } from '@workspace/api-backend'
import { ConfirmButton } from '@workspace/ui/components/confirm-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { ToggleField } from '@workspace/ui/components/toggle-field'
import { useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import {
  useDeleteMcpData,
  useMcpSettingsQuery,
  useUpdateMcpSettings,
} from '../hooks/use-investor-assistant'
import { FeedbackExampleDialog, LookupsExampleDialog } from './example-dialogs'

const PRIVACY_POLICY_URL = 'https://seedforge.com/privacy'

function SectionHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
    </div>
  )
}

function WhyWeDoThis({ children }: { children: string }) {
  return <p className="text-sm text-muted-foreground">Why we do this: {children}</p>
}

/** The alert email switch; alerts need the assistant, so it shows only while that is open. */
export function NotificationsSection() {
  const settings = useMcpSettingsQuery()
  const update = useUpdateMcpSettings()
  if (settings.data?.data.assistantEnabled !== true) return null

  return (
    <section className="space-y-3">
      <SectionHeading
        title="Notifications"
        description="How SeedForge tells you when something happens."
      />
      <ToggleField
        emphasis="off"
        label="Email me alerts"
        description="We email you the day a startup posts what you are waiting for, or a material update you follow. Alerts always arrive in the app and in your assistant’s journal."
        checked={!(settings.data.data.muteAlertEmails ?? false)}
        onChange={(checked) => {
          trackEvent('assistant_alert_emails_toggled', { muted: !checked, surface: 'settings' })
          update.mutate({ body: { muteAlertEmails: !checked } })
        }}
      />
      {update.isSuccess && <p className="text-sm text-muted-foreground">Saved</p>}
    </section>
  )
}

/** Privacy switches (while the assistant is open), the policy line, and export or delete. */
export function PrivacySection() {
  const settings = useMcpSettingsQuery()
  const update = useUpdateMcpSettings()
  const deleteAll = useDeleteMcpData()
  const [exporting, setExporting] = useState(false)
  const [deleted, setDeleted] = useState(false)
  const current = settings.data?.data

  async function exportData() {
    setExporting(true)
    try {
      const { data } = await v2InvestorExportMcpData({ throwOnError: true })
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'seedforge-ai-data.json'
      link.click()
      URL.revokeObjectURL(url)
      trackEvent('assistant_data_exported')
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <section className="space-y-3">
        <SectionHeading title="Privacy" description="We never train AI on your data." />
        {current?.assistantEnabled === true && (
          <>
            <ToggleField
              emphasis="off"
              label="Share anonymous feedback with founders"
              description="Your assistant’s reads, searches and checks in a startup’s record, and your conditions, can help founders improve. We turn them into anonymous feedback in our own words."
              checked={current.shareFeedback ?? true}
              onChange={(checked) => {
                trackEvent('assistant_privacy_setting_changed', {
                  setting: 'share_feedback',
                  enabled: checked,
                })
                update.mutate({ body: { shareFeedback: checked } })
              }}
            >
              <div className="space-y-1">
                <WhyWeDoThis>
                  it helps founders strengthen their business, including startups you may back.
                </WhyWeDoThis>
                <FeedbackExampleDialog />
              </div>
            </ToggleField>
            <ToggleField
              emphasis="off"
              label="Remember startups my assistant looked for"
              description="We remember only the names and websites of startups that are not on SeedForge. Your searches stay private and are not shared with anyone outside SeedForge."
              checked={current.keepLookups ?? true}
              onChange={(checked) => {
                trackEvent('assistant_privacy_setting_changed', {
                  setting: 'keep_lookups',
                  enabled: checked,
                })
                update.mutate({ body: { keepLookups: checked } })
              }}
            >
              <div className="space-y-1">
                <WhyWeDoThis>
                  your Discovery watchdog learns from them to improve its suggestions.
                </WhyWeDoThis>
                <LookupsExampleDialog />
              </div>
            </ToggleField>
            {update.isSuccess && <p className="text-sm text-muted-foreground">Saved</p>}
          </>
        )}
        <p className="text-sm text-muted-foreground">
          Invites to founders are kept until the founder joins or you remove them. They are included
          in your data export and deleted with your account.
        </p>
        <p className="text-sm text-muted-foreground">
          Your searches are kept for 90 days. They are included in your data export and deleted with
          your account.
        </p>
        <p className="text-sm text-muted-foreground">
          Your watchdog learns from your searches, from the startups you follow and pass, and from
          the focus you save.
        </p>
        <p className="text-sm text-muted-foreground">
          What we keep and for how long is in our{' '}
          <a className="underline" href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </p>
      </section>

      <section className="space-y-3">
        <SectionHeading title="Export or delete" />
        <div className="flex flex-wrap gap-3">
          <SecondaryButton size="sm" disabled={exporting} onClick={exportData}>
            {exporting ? 'Preparing...' : 'Export my data'}
          </SecondaryButton>
          <ConfirmButton
            confirmLabel="Delete everything?"
            disabled={deleteAll.isPending || deleted}
            onConfirm={() =>
              deleteAll.mutate(
                { body: { confirm: true } },
                {
                  onSuccess: () => {
                    trackEvent('assistant_data_deleted')
                    setDeleted(true)
                  },
                }
              )
            }
          >
            Delete my data
          </ConfirmButton>
        </div>
        <p className="text-sm text-muted-foreground">
          {deleted
            ? 'Your data was deleted. Your account is unchanged.'
            : 'Deleting removes your follows, notes, passes, thesis, alerts and call log. Your account stays.'}
        </p>
      </section>
    </>
  )
}
